/**
 * Blog Post 16: Hong Kong Stamp Duty 2026: Complete Guide for Property Buyers
 * 2026香港印花稅完整指南：買樓必知的稅項全攻略
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import AmazonRecommendation from '../components/common/AmazonRecommendation';
import SEO from '../components/common/SEO';

export default function BlogPost16() {
    const { i18n } = useCustomTranslation();
    const isEnglish = i18n.language === 'en';

    useEffect(() => {
        trackPageView('/blog/hong-kong-stamp-duty-guide-2026', 'Blog Post 16');
        document.title = isEnglish
            ? 'Hong Kong Stamp Duty 2026: Complete Guide for Property Buyers | PayWise365'
            : '2026香港印花稅完整指南：買樓必知的稅項全攻略 | PayWise365';
    }, [isEnglish]);

    const seoTitle = isEnglish
        ? 'Hong Kong Stamp Duty 2026: Complete Guide for Property Buyers | PayWise365'
        : '2026香港印花稅完整指南：買樓必知的稅項全攻略 | PayWise365';

    const seoDescription = isEnglish
        ? 'Complete guide to Hong Kong stamp duty 2026. Learn about BSD, AVD, SSD rates for first-time and non-first-time buyers. Includes calculation examples, exemptions, payment process, and strategies to minimize tax burden legally.'
        : '2026香港印花稅全面指南。了解從價印花稅、買家印花稅、額外印花稅的首次及非首次置業稅率。包括計算例子、豁免條件、繳付流程及合法節省稅款策略。';

    const seoKeywords = isEnglish
        ? 'Hong Kong stamp duty, 印花稅, stamp duty calculator, BSD, AVD, SSD, first time buyer stamp duty, property tax HK, stamp duty rates 2026, 從價印花稅, 買家印花稅, property purchase tax'
        : '香港印花稅, 從價印花稅, 買家印花稅, 額外印花稅, 首次置業印花稅, 印花稅計算, 物業稅, 樓市辣招, 印花稅率, 2026印花稅';

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            <SEO
                title={seoTitle}
                description={seoDescription}
                keywords={seoKeywords}
                canonicalUrl="https://www.paywise365.com/blog/hong-kong-stamp-duty-guide-2026"
                ogType="article"
                ogImage="https://www.paywise365.com/logo.svg"
                publishDate="2026-04-09T00:00:00+08:00"
                modifiedDate="2026-04-09T00:00:00+08:00"
                author="PayWise365"
                articleSection={isEnglish ? 'Property Tax Guide' : '物業稅務指南'}
                tags={isEnglish
                    ? ['Stamp Duty', 'Property Tax', 'Hong Kong Real Estate', 'First Time Buyer', 'Tax Planning']
                    : ['印花稅', '物業稅', '香港樓市', '首次置業', '稅務規劃']
                }
            />
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                    color: 'white',
                    py: { xs: 6, md: 8 },
                }}
            >
                <Container maxWidth="lg">
                    {/* Breadcrumb Navigation */}
                    <Box sx={{ mb: 3, fontSize: '14px', opacity: 0.9 }}>
                        <a href="/" style={{ color: 'white', textDecoration: 'none' }}>
                            {isEnglish ? 'Home' : '首頁'}
                        </a>
                        {' > '}
                        <a href="/blog" style={{ color: 'white', textDecoration: 'none' }}>
                            {isEnglish ? 'Blog' : '資訊'}
                        </a>
                        {' > '}
                        <span style={{ opacity: 0.8 }}>
                            {isEnglish ? 'Stamp Duty Guide' : '印花稅指南'}
                        </span>
                    </Box>

                    <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
                        {isEnglish
                            ? 'Hong Kong Stamp Duty 2026: Complete Guide for Property Buyers'
                            : '2026香港印花稅完整指南：買樓必知的稅項全攻略'}
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
                        {isEnglish
                            ? 'Master stamp duty calculations and save thousands on your property purchase'
                            : '掌握印花稅計算，在買樓時節省數萬至數十萬元'}
                    </Typography>
                </Container>
            </Box>

            {/* Article Content */}
            <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
                <Box
                    component="article"
                    sx={{
                        lineHeight: 1.8,
                        '& p': { mb: 2 },
                        '& h2': { mt: 4, mb: 2, fontSize: '1.75rem', fontWeight: 700 },
                        '& h3': { mt: 3, mb: 1.5, fontSize: '1.5rem', fontWeight: 600 },
                        '& ul, & ol': { mb: 2, pl: 3 },
                        '& li': { mb: 1 },
                    }}
                >
                    {!isEnglish ? (
                        // Traditional Chinese Content
                        <>
                            <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                                    <span style={{
                                        background: '#f3e8ff',
                                        color: '#7c3aed',
                                        padding: '6px 14px',
                                        borderRadius: '14px',
                                        fontWeight: '500',
                                        marginRight: '12px'
                                    }}>物業稅務指南</span>
                                    <span>發布日期：2026年4月9日</span>
                                    <span style={{ margin: '0 8px' }}>•</span>
                                    <span>閱讀時間：15 分鐘</span>
                                </div>
                            </div>

                            <h2>📋 引言：印花稅——買樓最易忽略的巨額開支</h2>
                            <p>
                                在香港買樓，很多人只計算首期和按揭供款，卻忽略了一個可能高達樓價15%的隱藏成本——<strong>印花稅</strong>。
                                對於一個HKD 8M的單位，印花稅可以從HKD 300,000（首次置業）到HKD 1,200,000（非首次置業），
                                相差接近HKD 900,000！
                            </p>
                            <p>
                                本文將為你全面拆解香港印花稅制度，包括：
                            </p>
                            <ul>
                                <li><strong>三大類印花稅：</strong>從價印花稅（AVD）、買家印花稅（BSD）、額外印花稅（SSD）</li>
                                <li><strong>首次vs非首次置業：</strong>稅率差異及定義</li>
                                <li><strong>實例計算：</strong>不同樓價及身份的稅款金額</li>
                                <li><strong>豁免條件：</strong>如何合法減免印花稅</li>
                                <li><strong>繳付流程：</strong>何時交、如何交、逾期罰則</li>
                                <li><strong>節稅策略：</strong>合法降低稅務負擔的方法</li>
                            </ul>

                            <h2>🏛️ 香港印花稅制度概覽（2026年版）</h2>
                            <h3>三大類印花稅</h3>
                            <p>香港物業交易涉及三種主要印花稅：</p>

                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#1e293b' }}>1. 從價印花稅（Ad Valorem Stamp Duty, AVD）</h4>
                                <p><strong>適用對象：</strong>所有物業交易</p>
                                <p><strong>稅率：</strong></p>
                                <ul style={{ marginBottom: '10px' }}>
                                    <li><strong>第2標準稅率（首次置業）：</strong>最高3.75%（累進制）</li>
                                    <li><strong>第1標準稅率（非首次置業/公司名義）：</strong>劃一15%</li>
                                </ul>
                                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: 0 }}>
                                    💡 這是最基本的印花稅，所有買家都要繳付
                                </p>
                            </div>

                            <div style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#991b1b' }}>2. 買家印花稅（Buyer's Stamp Duty, BSD）</h4>
                                <p><strong>適用對象：</strong>非香港永久居民、公司買家</p>
                                <p><strong>稅率：</strong>劃一15%（在AVD之上額外徵收）</p>
                                <p style={{ fontSize: '13px', color: '#991b1b', marginBottom: 0 }}>
                                    ⚠️ 外國人或公司買樓需同時繳付AVD + BSD，總稅率可達30%
                                </p>
                            </div>

                            <div style={{
                                background: '#fef3c7',
                                border: '1px solid #fcd34d',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#78350f' }}>3. 額外印花稅（Special Stamp Duty, SSD）</h4>
                                <p><strong>適用對象：</strong>3年內轉售的物業</p>
                                <p><strong>稅率：</strong>視乎持有期：</p>
                                <ul style={{ marginBottom: '10px' }}>
                                    <li>6個月內轉售：20%</li>
                                    <li>6-12個月：15%</li>
                                    <li>12-36個月：10%</li>
                                </ul>
                                <p style={{ fontSize: '13px', color: '#78350f', marginBottom: 0 }}>
                                    💡 持有超過3年則毋須繳付SSD
                                </p>
                            </div>

                            <h2>💰 從價印花稅（AVD）詳細稅率表</h2>
                            <h3>第2標準稅率（首次置業香港永久居民）</h3>
                            <p>這是最常用的稅率，適用於符合以下條件的買家：</p>
                            <ul>
                                <li>香港永久性居民</li>
                                <li>在購入時沒有擁有任何其他香港住宅物業</li>
                                <li>個人名義購買（非公司）</li>
                            </ul>

                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px',
                                overflowX: 'auto'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #cbd5e1', background: '#f1f5f9' }}>
                                            <th style={{ textAlign: 'left', padding: '12px 8px', fontWeight: '600' }}>物業價值</th>
                                            <th style={{ textAlign: 'right', padding: '12px 8px', fontWeight: '600' }}>稅率</th>
                                            <th style={{ textAlign: 'right', padding: '12px 8px', fontWeight: '600' }}>HKD 8M例子</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px' }}>HKD 3,000,000 或以下</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>HKD 100</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>—</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px' }}>HKD 3,000,001 - 3,528,240</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>HKD 100 + 超出HKD 3M部分之10%</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>—</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px' }}>HKD 3,528,241 - 4,500,000</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px', color: '#059669', fontWeight: '600' }}>1.5%</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>—</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px' }}>HKD 4,500,001 - 4,935,480</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>HKD 67,500 + 超出HKD 4.5M部分之10%</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>—</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px' }}>HKD 4,935,481 - 6,000,000</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px', color: '#059669', fontWeight: '600' }}>2.25%</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>—</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px' }}>HKD 6,000,001 - 6,720,000</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>HKD 135,000 + 超出HKD 6M部分之10%</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>—</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px' }}>HKD 6,720,001 - 20,000,000</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px', color: '#059669', fontWeight: '600' }}>3%</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px', color: '#059669', fontWeight: '700' }}>HKD 240,000</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px' }}>HKD 20,000,001 - 21,739,120</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>HKD 600,000 + 超出HKD 20M部分之10%</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>—</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '10px 8px' }}>HKD 21,739,121 或以上</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px', color: '#dc2626', fontWeight: '600' }}>3.75%</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>—</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <h3>第1標準稅率（非首次置業/公司買家）</h3>
                            <div style={{
                                background: '#fef2f2',
                                border: '2px solid #fca5a5',
                                borderRadius: '8px',
                                padding: '25px',
                                marginBottom: '20px',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '16px', color: '#991b1b', marginBottom: '10px', fontWeight: '600' }}>
                                    非首次置業或公司名義購買
                                </div>
                                <div style={{ fontSize: '48px', fontWeight: '700', color: '#dc2626', letterSpacing: '-1px' }}>
                                    15%
                                </div>
                                <div style={{ fontSize: '14px', color: '#991b1b', marginTop: '10px' }}>
                                    劃一稅率，無論樓價多少
                                </div>
                                <div style={{ fontSize: '13px', color: '#78350f', marginTop: '15px', padding: '12px', background: '#fef3c7', borderRadius: '6px' }}>
                                    💡 HKD 8M物業 = HKD 1,200,000印花稅<br />
                                    （比首次置業多付HKD 960,000）
                                </div>
                            </div>

                            <h2>📊 實例計算：不同情境下的印花稅</h2>

                            <h3>情境1：首次置業香港居民（最常見）</h3>
                            <div style={{
                                background: '#ecfdf5',
                                border: '1px solid #a7f3d0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>買家資料：</strong></p>
                                <ul>
                                    <li>香港永久居民</li>
                                    <li>首次置業（名下沒有其他物業）</li>
                                    <li>個人名義購買</li>
                                    <li>自住用途</li>
                                </ul>
                                <p><strong>物業價格：HKD 8,000,000</strong></p>
                                <p><strong>應繳印花稅：</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>從價印花稅（AVD）第2標準稅率：<strong style={{ color: '#059669', fontSize: '18px' }}>HKD 240,000</strong> (3%)</li>
                                    <li>買家印花稅（BSD）：豁免</li>
                                    <li>額外印花稅（SSD）：不適用（購入時）</li>
                                    <li><strong style={{ color: '#047857', fontSize: '20px' }}>總計：HKD 240,000</strong></li>
                                </ul>
                            </div>

                            <h3>情境2：已有物業的香港居民（換樓）</h3>
                            <div style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>買家資料：</strong></p>
                                <ul>
                                    <li>香港永久居民</li>
                                    <li>已擁有一個住宅物業</li>
                                    <li>個人名義購買</li>
                                </ul>
                                <p><strong>物業價格：HKD 8,000,000</strong></p>
                                <p><strong>應繳印花稅：</strong></p>
                                <ul style={{ marginBottom: '15px' }}>
                                    <li>從價印花稅（AVD）第1標準稅率：<strong style={{ color: '#dc2626', fontSize: '18px' }}>HKD 1,200,000</strong> (15%)</li>
                                    <li>買家印花稅（BSD）：豁免（香港永久居民）</li>
                                    <li><strong style={{ color: '#991b1b', fontSize: '20px' }}>總計：HKD 1,200,000</strong></li>
                                </ul>
                                <div style={{ background: '#fef3c7', padding: '15px', borderRadius: '6px', fontSize: '14px' }}>
                                    <strong>💡 先賣後買策略：</strong><br />
                                    如果在購入新物業後6個月內賣出舊物業，可以申請退回多付的印花稅（HKD 960,000），
                                    但需符合條件且及時提交申請。
                                </div>
                            </div>

                            <h3>情境3：非永久居民/外國人</h3>
                            <div style={{
                                background: '#fef2f2',
                                border: '2px solid #f87171',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>買家資料：</strong></p>
                                <ul>
                                    <li>非香港永久居民（如持工作簽證的外籍人士）</li>
                                    <li>個人名義購買</li>
                                </ul>
                                <p><strong>物業價格：HKD 8,000,000</strong></p>
                                <p><strong>應繳印花稅：</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>從價印花稅（AVD）第1標準稅率：HKD 1,200,000 (15%)</li>
                                    <li>買家印花稅（BSD）：<strong style={{ color: '#dc2626', fontSize: '18px' }}>HKD 1,200,000</strong> (15%)</li>
                                    <li><strong style={{ color: '#991b1b', fontSize: '22px' }}>總計：HKD 2,400,000</strong> (30%)</li>
                                </ul>
                            </div>

                            <h3>情境4：公司名義購買</h3>
                            <div style={{
                                background: '#fef2f2',
                                border: '2px solid #f87171',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>買家資料：</strong></p>
                                <ul>
                                    <li>有限公司名義購買</li>
                                    <li>無論公司董事國籍</li>
                                </ul>
                                <p><strong>物業價格：HKD 8,000,000</strong></p>
                                <p><strong>應繳印花稅：</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>從價印花稅（AVD）第1標準稅率：HKD 1,200,000 (15%)</li>
                                    <li>買家印花稅（BSD）：<strong style={{ color: '#dc2626', fontSize: '18px' }}>HKD 1,200,000</strong> (15%)</li>
                                    <li><strong style={{ color: '#991b1b', fontSize: '22px' }}>總計：HKD 2,400,000</strong> (30%)</li>
                                </ul>
                            </div>

                            <h2>🎯 如何確定「首次置業」身份？</h2>
                            <p>很多人對「首次置業」定義有誤解。以下是官方準則：</p>

                            <div style={{
                                background: '#eff6ff',
                                border: '1px solid #bfdbfe',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#1e40af' }}>✅ 符合「首次置業」條件（可享第2標準稅率）：</h4>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>香港永久性居民</li>
                                    <li>在購入物業時，<strong>未持有任何其他香港住宅物業</strong></li>
                                    <li>個人名義購買（非公司）</li>
                                    <li>作為唯一買家或所有買家均符合上述條件</li>
                                </ul>
                            </div>

                            <div style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#991b1b' }}>❌ 不符合「首次置業」條件（須繳第1標準稅率15%）：</h4>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>已擁有任何香港住宅物業（包括聯名持有、部分業權）</li>
                                    <li>即使舊物業尚未正式成交，只要已簽臨約就算持有</li>
                                    <li>配偶持有物業而自己沒有，本人購買仍可享首置稅率（但需注意按揭限制）</li>
                                    <li>持有車位、工商舖物業不影響首置身份</li>
                                    <li>曾經持有但已售出，再次購買仍可享首置稅率</li>
                                </ul>
                            </div>

                            <h3>常見誤解澄清</h3>
                            <div style={{
                                background: '#fef3c7',
                                border: '1px solid #fcd34d',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>❓ 問：我10年前買過樓但已賣出，現在再買算首置嗎？</strong></p>
                                <p><strong>✅ 答：</strong>算。「首次置業」定義是「購入時沒有持有其他物業」，不是「一生只能買一次樓」。
                                    只要你在簽新約時名下沒有其他住宅，就享有首置稅率。</p>

                                <p style={{ marginTop: '20px' }}><strong>❓ 問：我和配偶聯名買樓，但配偶已有另一個物業，稅率如何計？</strong></p>
                                <p><strong>❌ 答：</strong>須繳15%稅率。因為其中一位買家（配偶）不符合首置條件，整宗交易需按第1標準稅率徵收。</p>

                                <p style={{ marginTop: '20px' }}><strong>❓ 問：我持有內地或海外物業，在香港買樓算首置嗎？</strong></p>
                                <p><strong>✅ 答：</strong>算。印花稅只看<strong>香港住宅物業</strong>，海外物業不計算在內。</p>
                            </div>

                            <h2>💸 如何繳付印花稅？</h2>
                            <h3>繳付時限</h3>
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <ul style={{ marginBottom: 0 }}>
                                    <li><strong>簽署正式買賣合約後30天內</strong>必須繳付印花稅</li>
                                    <li>如在簽署臨時買賣合約後14天內簽署正式合約，可從簽臨約日起計30天</li>
                                    <li>逾期繳付會被罰款及加徵附加費</li>
                                </ul>
                            </div>

                            <h3>繳付方法</h3>
                            <ol>
                                <li><strong>律師代辦（最常見）：</strong>大部分買家委託律師代為處理印花稅申報及繳付</li>
                                <li><strong>網上繳付：</strong>透過稅務局「物業印花稅電子服務」系統</li>
                                <li><strong>親身前往：</strong>到稅務局印花稅署櫃位辦理</li>
                            </ol>

                            <h3>所需文件</h3>
                            <ul>
                                <li>買賣合約正本</li>
                                <li>買家及賣家身份證明文件副本</li>
                                <li>如屬首次置業，需提交「首次置業聲明書」（IRSD125A表格）</li>
                                <li>印花稅款項（支票/本票/現金/銀行轉賬）</li>
                            </ul>

                            <h3>逾期罰則</h3>
                            <div style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '30px'
                            }}>
                                <p><strong>逾期未繳印花稅的後果：</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li><strong>罰款：</strong>最高可達應繳稅款的10倍</li>
                                    <li><strong>附加費：</strong>
                                        <ul>
                                            <li>逾期1-2個月：稅款的2%</li>
                                            <li>逾期2-3個月：稅款的4%</li>
                                            <li>逾期超過3個月：稅款的10%</li>
                                        </ul>
                                    </li>
                                    <li><strong>法律後果：</strong>未加蓋印花的文件不具法律效力，不能在法庭作為證據</li>
                                    <li><strong>銀行按揭：</strong>銀行通常要求文件已加蓋印花才批出貸款</li>
                                </ul>
                            </div>

                            <h2>🎓 合法節省印花稅的策略</h2>

                            <h3>策略1：先賣後買（換樓人士）</h3>
                            <div style={{
                                background: '#ecfdf5',
                                border: '1px solid #a7f3d0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>適用情況：</strong>已有一個物業，想換樓</p>
                                <p><strong>操作方法：</strong></p>
                                <ol>
                                    <li>先賣出舊物業（完成成交）</li>
                                    <li>確保舊物業成交日早於新物業購入日</li>
                                    <li>購入新物業時即可享首置稅率</li>
                                </ol>
                                <p><strong>節省金額（HKD 8M物業）：</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>第1標準稅率：HKD 1,200,000</li>
                                    <li>第2標準稅率：HKD 240,000</li>
                                    <li><strong style={{ color: '#059669', fontSize: '18px' }}>節省：HKD 960,000</strong></li>
                                </ul>
                            </div>

                            <h3>策略2：先買後賣退稅（需符合條件）</h3>
                            <div style={{
                                background: '#eff6ff',
                                border: '1px solid #bfdbfe',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>適用情況：</strong>需要同時持有兩個物業一段時間（如裝修、搬遷）</p>
                                <p><strong>操作方法：</strong></p>
                                <ol>
                                    <li>先購入新物業（繳付15%稅率）</li>
                                    <li><strong>在購入新物業後6個月內</strong>賣出舊物業並完成成交</li>
                                    <li>向稅務局申請退回多付的印花稅</li>
                                </ol>
                                <p><strong>注意事項：</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>必須在購入新物業起計6個月內完成舊物業成交</li>
                                    <li>需在舊物業成交後2年內提交退稅申請</li>
                                    <li>新舊物業均須為住宅</li>
                                    <li>買賣雙方身份必須相同</li>
                                </ul>
                            </div>

                            <h3>策略3：提名近親轉名（謹慎使用）</h3>
                            <div style={{
                                background: '#fef3c7',
                                border: '1px solid #fcd34d',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>適用情況：</strong>與配偶或父母子女聯名購買</p>
                                <p><strong>操作方法：</strong></p>
                                <ul>
                                    <li>由符合首置條件的家庭成員單獨購入</li>
                                    <li>日後再以「近親轉讓」方式（較低稅率）加入另一方名字</li>
                                </ul>
                                <p style={{ fontSize: '13px', color: '#78350f', marginBottom: 0 }}>
                                    ⚠️ <strong>注意：</strong>這涉及按揭、法律等複雜問題，必須諮詢專業人士。
                                    而且政府可能視為避稅而追討稅款。
                                </p>
                            </div>

                            <h3>策略4：考慮車位獨立成交</h3>
                            <div style={{
                                background: '#ecfdf5',
                                border: '1px solid #a7f3d0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>適用情況：</strong>購買連車位的物業</p>
                                <p><strong>節稅原理：</strong></p>
                                <ul>
                                    <li>車位不屬「住宅物業」，不受辣招印花稅影響</li>
                                    <li>如果車位佔總價較高比例，可考慮分開成交</li>
                                    <li>住宅部分按相應稅率徵稅，車位部分按較低的商業物業稅率</li>
                                </ul>
                                <p><strong>例子：</strong></p>
                                <ul style={{ marginBottom: 0 }}>
                                    <li>物業連車位總價：HKD 9,000,000</li>
                                    <li>住宅：HKD 8,000,000，車位：HKD 1,000,000</li>
                                    <li>分開成交可能略為節省稅款，但需考慮額外的法律及行政費用</li>
                                </ul>
                            </div>

                            <h2>❓ 常見問題解答（FAQ）</h2>

                            <h3>Q1: 我簽了臨時買賣合約後反悔，印花稅如何處理？</h3>
                            <p>
                                <strong>A:</strong> 如果在未簽正式合約前取消交易：<br />
                                • 臨約印花稅（通常為樓價0.1%）已繳不能退回<br />
                                • 訂金被沒收<br />
                                • 正式合約印花稅無需繳付（因沒有簽正式合約）
                            </p>

                            <h3>Q2: 居屋、公屋轉名需要繳印花稅嗎？</h3>
                            <p>
                                <strong>A:</strong> 需要，但稅率較低：<br />
                                • 居屋首次轉讓（由房委會購入）：豁免印花稅<br />
                                • 居屋第二市場轉讓：需繳印花稅，但按樓價（非市價）計算<br />
                                • 公屋租約轉名：不涉及印花稅<br />
                                • 公屋「租置計劃」購買：有特別安排，稅率較低
                            </p>

                            <h3>Q3: 送契（零元轉名給親人）需要繳印花稅嗎？</h3>
                            <p>
                                <strong>A:</strong> 需要，而且可能更高：<br />
                                • 近親之間（父母子女、夫妻、兄弟姊妹）：按第2標準稅率計算<br />
                                • 非近親之間：按市價計算，並須付AVD + BSD（如適用）<br />
                                • 印花稅按<strong>物業市值</strong>計算，而非轉讓代價<br />
                                • 即使免費送出，仍需按市值繳稅
                            </p>

                            <h3>Q4: 買入一手樓花，何時需要繳印花稅？</h3>
                            <p>
                                <strong>A:</strong> 分兩階段繳付：<br />
                                • <strong>臨約階段：</strong>簽臨約後30天內繳付臨約印花稅（樓價的0.25%或HKD 100，取較高者）<br />
                                • <strong>正式合約階段：</strong>簽正式合約後30天內繳付正式印花稅（可扣除已付的臨約印花稅）<br />
                                • 大部分發展商會協助處理
                            </p>

                            <h3>Q5: 我是永久居民但持外國護照，可享首置稅率嗎？</h3>
                            <p>
                                <strong>A:</strong> 可以。關鍵是「香港永久性居民」身份，與持有哪國護照無關。<br />
                                只要你持有香港永久性居民身份證，並符合首置條件（名下無其他物業），即可享用第2標準稅率。
                            </p>

                            <h3>Q6: 買入工商舖物業，印花稅如何計算？</h3>
                            <p>
                                <strong>A:</strong> 工商舖不受「辣招」影響：<br />
                                • 不論買家身份，一律按第2標準稅率（最高3.75%）<br />
                                • 無須繳付買家印花稅（BSD）<br />
                                • 額外印花稅（SSD）不適用於非住宅物業<br />
                                • 車位、工廈、商舖、寫字樓均屬非住宅，稅率較低
                            </p>

                            <h3>Q7: 如果錯誤申報首置身份，後果如何？</h3>
                            <p>
                                <strong>A:</strong> 後果嚴重：<br />
                                • 稅務局會追討差額稅款（15% - 3.75% = 11.25%）<br />
                                • 罰款可達差額稅款的3倍<br />
                                • 可能被刑事檢控，最高罰款HKD 10,000及監禁6個月<br />
                                • 律師可能被紀律處分<br />
                                建議：如不確定是否符合首置資格，向律師或稅務局查詢
                            </p>

                            <h3>Q8: 聯名購買物業，印花稅可以分開計算嗎？</h3>
                            <p>
                                <strong>A:</strong> 不可以：<br />
                                • 印花稅以整個物業的成交價計算<br />
                                • 即使多人聯名，只計算一次印花稅<br />
                                • <strong>但所有買家必須全部符合首置條件，才可享第2標準稅率</strong><br />
                                • 只要其中一人不符合（如已有物業），全部要按15%計<br />
                                • 稅款可由買家之間自行協定分擔比例
                            </p>

                            <h2>🎯 總結及建議</h2>
                            <div style={{
                                background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                                border: '2px solid #a855f7',
                                borderRadius: '12px',
                                padding: '30px',
                                marginBottom: '30px'
                            }}>
                                <h3 style={{ marginTop: 0, color: '#6b21a8' }}>💡 關鍵要點</h3>
                                <ul style={{ marginBottom: '20px' }}>
                                    <li><strong>印花稅是買樓的重大開支</strong>——HKD 8M物業可相差HKD 960,000</li>
                                    <li><strong>首置身份非常重要</strong>——3.75% vs 15%的巨大差異</li>
                                    <li><strong>準確申報很重要</strong>——錯誤申報可招致刑責</li>
                                    <li><strong>計劃換樓要小心</strong>——先賣後買可節省近百萬</li>
                                    <li><strong>30天期限要緊記</strong>——逾期罰款及附加費很重</li>
                                </ul>

                                <h3 style={{ color: '#6b21a8' }}>📝 購樓前準備清單</h3>
                                <ol style={{ marginBottom: 0 }}>
                                    <li>確認自己是否符合首置條件</li>
                                    <li>使用<a href="/calculators/house-loan" style={{ color: '#7c3aed', textDecoration: 'underline', fontWeight: '600' }}>按揭計算機</a>計算印花稅金額</li>
                                    <li>預留足夠現金繳付印花稅（不能計入按揭）</li>
                                    <li>如有疑問，諮詢律師或稅務顧問</li>
                                    <li>換樓人士考慮先賣後買策略</li>
                                    <li>準備好所需文件</li>
                                    <li>記下繳付限期，避免逾期</li>
                                </ol>
                            </div>

                            <div style={{
                                background: '#fef3c7',
                                border: '1px solid #fcd34d',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '30px'
                            }}>
                                <p style={{ fontWeight: '600', color: '#78350f', marginBottom: '10px' }}>⚠️ 免責聲明</p>
                                <p style={{ fontSize: '13px', marginBottom: 0 }}>
                                    本文提供的資訊僅供參考，不構成法律或稅務建議。印花稅法例可能隨時修改，
                                    實際應繳稅款應以稅務局最新公布及個別情況為準。
                                    在作出任何財務決定前，請諮詢專業律師或稅務顧問。
                                </p>
                            </div>

                            {/* Internal Links */}
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '30px'
                            }}>
                                <h3 style={{ fontSize: '18px', marginTop: 0 }}>📚 相關文章</h3>
                                <ul style={{ marginBottom: 0, listStyle: 'none', paddingLeft: 0 }}>
                                    <li style={{ marginBottom: '10px' }}>
                                        🏠 <a href="/blog/first-time-homebuyer-mortgage-guide-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            首次置業按揭全攻略
                                        </a>
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        💰 <a href="/blog/true-cost-5-million-mortgage-hong-kong-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            500萬港元按揭的真實成本
                                        </a>
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        📊 <a href="/blog/rent-vs-buy-hong-kong-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            租樓vs買樓完整財務分析
                                        </a>
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        🏦 <a href="/blog/2026-hong-kong-best-mortgage-rates" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            2026年香港最優惠按揭利率比較
                                        </a>
                                    </li>
                                    <li>
                                        🔄 <a href="/blog/mortgage-refinancing-hong-kong-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            按揭轉按全攻略
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            {/* CTA Section */}
                            <div style={{
                                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                                color: 'white',
                                borderRadius: '12px',
                                padding: '30px',
                                textAlign: 'center',
                                marginBottom: '40px'
                            }}>
                                <h3 style={{ marginTop: 0, fontSize: '24px' }}>🧮 計算你的買樓總成本</h3>
                                <p style={{ fontSize: '16px', marginBottom: '20px', opacity: 0.95 }}>
                                    使用我們的免費計算機，準確計算印花稅、首期、律師費等所有開支
                                </p>
                                <a
                                    href="/calculators/house-loan"
                                    style={{
                                        background: 'white',
                                        color: '#7c3aed',
                                        padding: '12px 30px',
                                        borderRadius: '8px',
                                        textDecoration: 'none',
                                        fontWeight: '600',
                                        fontSize: '16px',
                                        display: 'inline-block'
                                    }}
                                >
                                    免費按揭計算機 →
                                </a>
                            </div>
                        </>
                    ) : (
                        // English Content
                        <>
                            <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                                    <span style={{
                                        background: '#f3e8ff',
                                        color: '#7c3aed',
                                        padding: '6px 14px',
                                        borderRadius: '14px',
                                        fontWeight: '500',
                                        marginRight: '12px'
                                    }}>Property Tax Guide</span>
                                    <span>Published: April 9, 2026</span>
                                    <span style={{ margin: '0 8px' }}>•</span>
                                    <span>Reading Time: 15 min</span>
                                </div>
                            </div>

                            <h2>📋 Introduction: Stamp Duty—The Hidden Cost of Property Purchase</h2>
                            <p>
                                When buying property in Hong Kong, many people calculate only the down payment and mortgage payments,
                                overlooking a potentially massive hidden cost—<strong>stamp duty</strong>, which can reach up to 15% of the property price.
                                For an HKD 8M unit, stamp duty ranges from HKD 300,000 (first-time buyers) to HKD 1,200,000 (non-first-timers),
                                a difference of nearly HKD 900,000!
                            </p>
                            <p>This comprehensive guide covers:</p>
                            <ul>
                                <li><strong>Three Types of Stamp Duty:</strong> AVD, BSD, SSD</li>
                                <li><strong>First-time vs Non-first-time Rates:</strong> Tax differences and definitions</li>
                                <li><strong>Calculation Examples:</strong> Tax amounts for different prices and buyer profiles</li>
                                <li><strong>Exemption Conditions:</strong> How to legally reduce stamp duty</li>
                                <li><strong>Payment Process:</strong> When, how, and penalties for late payment</li>
                                <li><strong>Tax Strategies:</strong> Legal methods to minimize tax burden</li>
                            </ul>

                            <h2>🏛️ Hong Kong Stamp Duty System Overview (2026)</h2>
                            <h3>Three Main Types of Stamp Duty</h3>

                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#1e293b' }}>1. Ad Valorem Stamp Duty (AVD)</h4>
                                <p><strong>Applies to:</strong> All property transactions</p>
                                <p><strong>Rates:</strong></p>
                                <ul>
                                    <li><strong>Scale 2 Rate (First-time buyers):</strong> Up to 3.75% (progressive)</li>
                                    <li><strong>Scale 1 Rate (Non-first-time/Company):</strong> Flat 15%</li>
                                </ul>
                            </div>

                            <div style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#991b1b' }}>2. Buyer's Stamp Duty (BSD)</h4>
                                <p><strong>Applies to:</strong> Non-permanent residents, Company buyers</p>
                                <p><strong>Rate:</strong> Flat 15% (additional to AVD)</p>
                                <p style={{ fontSize: '13px', color: '#991b1b', marginBottom: 0 }}>
                                    ⚠️ Foreigners or companies pay AVD + BSD, total can reach 30%
                                </p>
                            </div>

                            <div style={{
                                background: '#fef3c7',
                                border: '1px solid #fcd34d',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <h4 style={{ marginTop: 0, color: '#78350f' }}>3. Special Stamp Duty (SSD)</h4>
                                <p><strong>Applies to:</strong> Properties resold within 3 years</p>
                                <p><strong>Rates:</strong></p>
                                <ul>
                                    <li>Within 6 months: 20%</li>
                                    <li>6-12 months: 15%</li>
                                    <li>12-36 months: 10%</li>
                                </ul>
                            </div>

                            <h2>💰 AVD Scale 2 Rate Table (First-time Buyers)</h2>
                            <p>Most commonly used rate for Hong Kong permanent residents buying their first property:</p>

                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px',
                                overflowX: 'auto'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #cbd5e1', background: '#f1f5f9' }}>
                                            <th style={{ textAlign: 'left', padding: '12px 8px' }}>Property Value</th>
                                            <th style={{ textAlign: 'right', padding: '12px 8px' }}>Rate</th>
                                            <th style={{ textAlign: 'right', padding: '12px 8px' }}>HKD 8M Example</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                            <td style={{ padding: '10px 8px' }}>Up to HKD 3,000,000</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>HKD 100</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>—</td>
                                        </tr>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', background: '#fafafa' }}>
                                            <td style={{ padding: '10px 8px' }}>HKD 6,720,001 - 20,000,000</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px', color: '#059669', fontWeight: '600' }}>3%</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px', color: '#059669', fontWeight: '700' }}>HKD 240,000</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '10px 8px' }}>Above HKD 21,739,121</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px', color: '#dc2626', fontWeight: '600' }}>3.75%</td>
                                            <td style={{ textAlign: 'right', padding: '10px 8px' }}>—</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <h3>Scale 1 Rate (Non-first-time/Company Buyers)</h3>
                            <div style={{
                                background: '#fef2f2',
                                border: '2px solid #fca5a5',
                                borderRadius: '8px',
                                padding: '25px',
                                marginBottom: '20px',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '48px', fontWeight: '700', color: '#dc2626' }}>15%</div>
                                <div style={{ fontSize: '14px', color: '#991b1b', marginTop: '10px' }}>
                                    Flat rate regardless of property price
                                </div>
                                <div style={{ fontSize: '13px', color: '#78350f', marginTop: '15px', padding: '12px', background: '#fef3c7', borderRadius: '6px' }}>
                                    💡 HKD 8M property = HKD 1,200,000 stamp duty<br />
                                    (HKD 960,000 more than first-time buyers)
                                </div>
                            </div>

                            <h2>📊 Calculation Examples</h2>

                            <h3>Scenario 1: First-time Hong Kong Resident (Most Common)</h3>
                            <div style={{
                                background: '#ecfdf5',
                                border: '1px solid #a7f3d0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>Buyer Profile:</strong></p>
                                <ul>
                                    <li>Hong Kong permanent resident</li>
                                    <li>First-time buyer (no other properties)</li>
                                    <li>Individual purchase</li>
                                </ul>
                                <p><strong>Property Price: HKD 8,000,000</strong></p>
                                <p><strong>Stamp Duty:</strong></p>
                                <ul>
                                    <li>AVD Scale 2: <strong style={{ color: '#059669', fontSize: '18px' }}>HKD 240,000</strong> (3%)</li>
                                    <li>BSD: Exempt</li>
                                    <li><strong style={{ color: '#047857', fontSize: '20px' }}>Total: HKD 240,000</strong></li>
                                </ul>
                            </div>

                            <h3>Scenario 2: Existing Property Owner (Upgrading)</h3>
                            <div style={{
                                background: '#fef2f2',
                                border: '1px solid #fecaca',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>Buyer Profile:</strong> HK resident with existing property</p>
                                <p><strong>Property Price: HKD 8,000,000</strong></p>
                                <p><strong>Stamp Duty:</strong></p>
                                <ul>
                                    <li>AVD Scale 1: <strong style={{ color: '#dc2626', fontSize: '18px' }}>HKD 1,200,000</strong> (15%)</li>
                                    <li><strong style={{ color: '#991b1b', fontSize: '20px' }}>Total: HKD 1,200,000</strong></li>
                                </ul>
                                <p style={{ background: '#fef3c7', padding: '15px', borderRadius: '6px', fontSize: '14px' }}>
                                    💡 <strong>Sell-then-buy strategy:</strong> If you sell your old property within 6 months after buying the new one,
                                    you can apply for a refund of the excess stamp duty (HKD 960,000).
                                </p>
                            </div>

                            <h3>Scenario 3: Non-permanent Resident/Foreigner</h3>
                            <div style={{
                                background: '#fef2f2',
                                border: '2px solid #f87171',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '20px'
                            }}>
                                <p><strong>Property Price: HKD 8,000,000</strong></p>
                                <p><strong>Stamp Duty:</strong></p>
                                <ul>
                                    <li>AVD Scale 1: HKD 1,200,000 (15%)</li>
                                    <li>BSD: <strong style={{ color: '#dc2626', fontSize: '18px' }}>HKD 1,200,000</strong> (15%)</li>
                                    <li><strong style={{ color: '#991b1b', fontSize: '22px' }}>Total: HKD 2,400,000</strong> (30%)</li>
                                </ul>
                            </div>

                            <h2>❓ Frequently Asked Questions</h2>

                            <h3>Q1: I cancelled after signing preliminary agreement. What about stamp duty?</h3>
                            <p>
                                <strong>A:</strong> Preliminary agreement stamp duty (usually 0.1% of price) is non-refundable.
                                Deposit is forfeited. Formal agreement stamp duty not required since no formal contract was signed.
                            </p>

                            <h3>Q2: Do I need to pay stamp duty for HOS (Home Ownership Scheme) transfers?</h3>
                            <p>
                                <strong>A:</strong> Yes, but at lower rates. First transfer from Housing Authority is exempt.
                                Second market transfers require stamp duty but calculated on subsidized price, not market value.
                            </p>

                            <h3>Q3: Property gift to family member (zero consideration)—stamp duty required?</h3>
                            <p>
                                <strong>A:</strong> Yes. Close relatives (parents, children, spouses, siblings) pay Scale 2 rates.
                                Non-close relatives pay based on market value with AVD + BSD (if applicable).
                                Stamp duty calculated on <strong>market value</strong>, not transfer consideration, even if free.
                            </p>

                            <h2>🎯 Summary & Recommendations</h2>
                            <div style={{
                                background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)',
                                border: '2px solid #a855f7',
                                borderRadius: '12px',
                                padding: '30px',
                                marginBottom: '30px'
                            }}>
                                <h3 style={{ marginTop: 0, color: '#6b21a8' }}>💡 Key Takeaways</h3>
                                <ul style={{ marginBottom: '20px' }}>
                                    <li><strong>Stamp duty is a major expense</strong>—HKD 8M property can differ by HKD 960,000</li>
                                    <li><strong>First-time status is crucial</strong>—3.75% vs 15% massive difference</li>
                                    <li><strong>Accurate declaration important</strong>—False claims may result in criminal charges</li>
                                    <li><strong>Upgrading requires careful planning</strong>—Sell-then-buy can save nearly HKD 1M</li>
                                    <li><strong>Remember 30-day deadline</strong>—Late payment incurs penalties</li>
                                </ul>

                                <h3 style={{ color: '#6b21a8' }}>📝 Pre-purchase Checklist</h3>
                                <ol>
                                    <li>Confirm first-time buyer eligibility</li>
                                    <li>Use <a href="/calculators/house-loan" style={{ color: '#7c3aed', textDecoration: 'underline', fontWeight: '600' }}>mortgage calculator</a> to calculate stamp duty</li>
                                    <li>Reserve sufficient cash for stamp duty (cannot be mortgaged)</li>
                                    <li>Consult lawyer or tax advisor if uncertain</li>
                                    <li>Consider sell-then-buy strategy for upgraders</li>
                                    <li>Prepare required documents</li>
                                    <li>Note payment deadline to avoid late penalties</li>
                                </ol>
                            </div>

                            {/* Internal Links */}
                            <div style={{
                                background: '#f8fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                padding: '20px',
                                marginBottom: '30px'
                            }}>
                                <h3 style={{ fontSize: '18px', marginTop: 0 }}>📚 Related Articles</h3>
                                <ul style={{ marginBottom: 0, listStyle: 'none', paddingLeft: 0 }}>
                                    <li style={{ marginBottom: '10px' }}>
                                        🏠 <a href="/blog/first-time-homebuyer-mortgage-guide-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            First-Time Homebuyer Mortgage Guide
                                        </a>
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        💰 <a href="/blog/true-cost-5-million-mortgage-hong-kong-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            True Cost of HKD 5 Million Mortgage
                                        </a>
                                    </li>
                                    <li style={{ marginBottom: '10px' }}>
                                        📊 <a href="/blog/rent-vs-buy-hong-kong-2026" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            Rent vs Buy Complete Financial Analysis
                                        </a>
                                    </li>
                                    <li>
                                        🏦 <a href="/blog/2026-hong-kong-best-mortgage-rates" style={{ color: '#2563eb', textDecoration: 'none' }}>
                                            Hong Kong Best Mortgage Rates 2026
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            {/* CTA Section */}
                            <div style={{
                                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                                color: 'white',
                                borderRadius: '12px',
                                padding: '30px',
                                textAlign: 'center',
                                marginBottom: '40px'
                            }}>
                                <h3 style={{ marginTop: 0, fontSize: '24px' }}>🧮 Calculate Your Total Property Cost</h3>
                                <p style={{ fontSize: '16px', marginBottom: '20px', opacity: 0.95 }}>
                                    Use our free calculator to accurately calculate stamp duty, down payment, legal fees, and all expenses
                                </p>
                                <a
                                    href="/calculators/house-loan"
                                    style={{
                                        background: 'white',
                                        color: '#7c3aed',
                                        padding: '12px 30px',
                                        borderRadius: '8px',
                                        textDecoration: 'none',
                                        fontWeight: '600',
                                        fontSize: '16px',
                                        display: 'inline-block'
                                    }}
                                >
                                    Free Mortgage Calculator →
                                </a>
                            </div>
                        </>
                    )}
                </Box>

                {/* Amazon Recommendation */}
                <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #e2e8f0' }}>
                    <AmazonRecommendation isEnglish={isEnglish} />
                </Box>
            </Container>
        </Box>
    );
}
